import React, { useState, useEffect } from 'react';
import { Thermometer, Battery } from 'lucide-react';
import { Device } from '../../types';
import deviceService from '../../services/deviceService';
import {
  SensorPanelsContainer,
  LeftColumn,
  RightColumn,
  SensorPanel,
  PanelHeader,
  PanelIcon,
  PanelTitle,
  SensorList,
  SensorRow,
  SensorLabel,
  SensorValue
} from '../../styles/SensorPanelStyles';

interface NetatmoOutdoorDetailsProps {
  device: Device;
  deviceId: string;
}

interface NetatmoData {
  temperature?: number;
  humidity?: number;
  battery_percent?: number;
  rf_status?: number;
  last_seen?: string;
  min_temp?: number;
  max_temp?: number;
}

const NetatmoOutdoorDetails: React.FC<NetatmoOutdoorDetailsProps> = ({ device, deviceId }) => {
  const [latestData, setLatestData] = useState<NetatmoData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestData = async () => {
      try {
        const data = await deviceService.getLatestData(deviceId);
        if (data && data.decoded_json) {
          setLatestData(data.decoded_json);
        }
      } catch (error) {
        console.error('Error fetching latest data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestData();
  }, [deviceId]);

  const formatTemperature = (temp: number | undefined): string => {
    return temp !== undefined ? `${temp.toFixed(1)}°C` : 'N/A';
  };

  const formatHumidity = (humidity: number | undefined): string => {
    return humidity !== undefined ? `${humidity.toFixed(0)}%` : 'N/A';
  };

  const formatBattery = (battery: number | undefined): string => {
    return battery !== undefined ? `${battery.toFixed(0)}%` : 'N/A';
  };

  const calculateDewPoint = (temp: number | undefined, humidity: number | undefined): string => {
    if (temp === undefined || humidity === undefined) return 'N/A';
    const dewPoint = temp - ((100 - humidity) / 5);
    return `${dewPoint.toFixed(1)}°C`;
  };

  const getBatteryStatus = (battery: number | undefined): 'normal' | 'warning' | 'critical' => {
    if (battery === undefined) return 'normal';
    if (battery > 60) return 'normal';
    if (battery > 20) return 'warning';
    return 'critical';
  };

  const getSignalQuality = (rfStatus: number | undefined): string => {
    if (rfStatus === undefined) return 'Unknown';
    if (rfStatus > 80) return 'Excellent';
    if (rfStatus > 60) return 'Good';
    if (rfStatus > 40) return 'Fair';
    return 'Weak';
  };

  if (loading) {
    return (
      <SensorPanelsContainer>
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
          Loading outdoor module data...
        </div>
      </SensorPanelsContainer>
    );
  }

  return (
    <SensorPanelsContainer>
      <LeftColumn>
        <SensorPanel>
          <PanelHeader>
            <PanelIcon>
              <Thermometer size={16} />
            </PanelIcon>
            <PanelTitle>Weather Conditions</PanelTitle>
          </PanelHeader>
          <SensorList>
            <SensorRow>
              <SensorLabel>Outdoor Temperature</SensorLabel>
              <SensorValue status="normal">{formatTemperature(latestData?.temperature)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Humidity</SensorLabel>
              <SensorValue status="normal">{formatHumidity(latestData?.humidity)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Dew Point</SensorLabel>
              <SensorValue status="normal">{calculateDewPoint(latestData?.temperature, latestData?.humidity)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Min Temperature (24h)</SensorLabel>
              <SensorValue status={latestData?.min_temp !== undefined ? 'normal' : 'inactive'}>{formatTemperature(latestData?.min_temp)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Max Temperature (24h)</SensorLabel>
              <SensorValue status={latestData?.max_temp !== undefined ? 'normal' : 'inactive'}>{formatTemperature(latestData?.max_temp)}</SensorValue>
            </SensorRow>
          </SensorList>
        </SensorPanel>
      </LeftColumn>

      <RightColumn>
        <SensorPanel>
          <PanelHeader>
            <PanelIcon>
              <Battery size={16} />
            </PanelIcon>
            <PanelTitle>Device Health</PanelTitle>
          </PanelHeader>
          <SensorList>
            <SensorRow>
              <SensorLabel>Battery Level</SensorLabel>
              <SensorValue status={getBatteryStatus(latestData?.battery_percent)}>{formatBattery(latestData?.battery_percent)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Battery Status</SensorLabel>
              <SensorValue status={getBatteryStatus(latestData?.battery_percent)}>
                {latestData?.battery_percent === undefined ? 'Unknown' :
                 latestData.battery_percent > 60 ? 'Good' :
                 latestData.battery_percent > 20 ? 'Low' :
                 'Critical'}
              </SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Radio Signal Quality</SensorLabel>
              <SensorValue status="normal">{getSignalQuality(latestData?.rf_status)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Last Communication</SensorLabel>
              <SensorValue status="normal">
                {latestData?.last_seen ? new Date(latestData.last_seen).toLocaleString() : 'N/A'}
              </SensorValue>
            </SensorRow>
          </SensorList>
        </SensorPanel>
      </RightColumn>
    </SensorPanelsContainer>
  );
};

export default NetatmoOutdoorDetails;
